/**
 * aethereum changelog — print the team's recent NOTABLE changes.
 *
 * A running, scannable, chronological log of what the team's agents have
 * changed: contracts declared/changed, decisions, finished plan steps, closed
 * tickets, settled negotiations, goals, verifications, briefs. DERIVED from the
 * room's existing events (no new event type) and surfaced newest-first so any
 * agent or human can glance at it and catch conflicts. Read-only (peek): never
 * advances the alert cursor. Fails soft offline.
 */

import { loadAutoConfig } from "../hive/auto.js";

export interface ChangelogOptions {
  cwd?: string;
  env?: Record<string, string | undefined>;
  fetchImpl?: typeof fetch;
}

interface ChangelogRow {
  ts: number;
  actor: string;
  kind: string;
  summary: string;
}

/** A coarse relative time for the printed log (deterministic given `now`). */
function relativeTime(ts: number, now: number): string {
  const diff = Math.max(0, now - ts);
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

/** Render the changelog newest-first as a clean mono block. Exported for tests. */
export function renderChangelog(rows: ChangelogRow[], now: number = Date.now()): string {
  if (!rows.length) return "No changes yet. The team hasn't changed anything notable.";
  const lines = ["Team changelog — recent changes (newest first):", ""];
  // The API returns newest LAST; print newest first.
  for (const r of [...rows].reverse()) {
    lines.push(`  • ${r.actor} ${r.summary}  (${relativeTime(r.ts, now)})`);
  }
  return lines.join("\n");
}

export async function runChangelog(options: ChangelogOptions = {}): Promise<void> {
  const cwd = options.cwd ?? process.cwd();
  const env = options.env ?? (process.env as Record<string, string | undefined>);
  const fetchImpl = options.fetchImpl ?? fetch;

  // Probe config even if AETHEREUM_AUTO=off (this is an explicit read).
  const cfg = loadAutoConfig(cwd, { ...env, AETHEREUM_AUTO: "" });
  if (!cfg) {
    console.error("No Aethereum room configured here. Run 'aethereum init --token <key>' first.");
    process.exit(1);
    return;
  }

  try {
    const res = await fetchImpl(`${cfg.url}/api/sync/context?peek=1`, {
      headers: { Authorization: `Bearer ${cfg.token}` },
      signal: AbortSignal.timeout(5_000),
    });
    if (!res.ok) {
      console.error(
        res.status === 403 || res.status === 401
          ? "Token rejected (revoked or wrong room). Reconnect from the dashboard."
          : `Could not reach Aethereum (${res.status}).`,
      );
      process.exit(1);
      return;
    }
    const body = (await res.json()) as { changelog?: ChangelogRow[] };
    console.log(renderChangelog(body.changelog ?? []));
  } catch {
    // Fail soft: never break the user's flow on a network hiccup.
    console.error("Could not reach Aethereum. Check your network.");
    process.exit(1);
  }
}
